import { c as _c } from "react/compiler-runtime";
import { Sponsor } from 'api-graphql-types';
import { type RowRange } from 'design-system/client';
import { ErrorMessage } from 'indexed-db/errors';
import { constructRandomAccessMessage } from 'indexed-db/messages';
import { useCallback, useEffect, useEffectEvent, useState } from 'react';
import type { FEPPContentDonation } from '../types';
import { useGetDonations, useGetLoadedDonationCount, useGetTotalDonationAmount, useGetTotalDonationCount, useInsertInitialDonations, useSetAllNewDonationsAsSeen } from './dbOperations';
import DonationFeed from './DonationFeed';
import Progress from './Progress';
import { ProgressAndDonationsProvider } from './ProgressAndDonationsContext';
import useHandleDbError from './useHandleDbError';
import useDebounce from '@/helpers/hooks/useDebounce';
import useIndexedDbWorker from '@/helpers/hooks/useIndexedDbWorker';
import { Maybe } from '@/types';
const workerUrl = new URL('indexed-db/donationFeedWorker', import.meta.url);
function useOnViewNewDonations(fundraisingEventId, currentRow, donationCount) {
  const $ = _c(9);
  const handleAllDonationsSeen = useSetAllNewDonationsAsSeen(fundraisingEventId);
  let t0;
  if ($[0] !== handleAllDonationsSeen) {
    t0 = () => {
      setTimeout(() => {
        handleAllDonationsSeen();
      }, 3000);
    };
    $[0] = handleAllDonationsSeen;
    $[1] = t0;
  } else {
    t0 = $[1];
  }
  const triggerHandleAllDonationsSeen = useEffectEvent(t0);
  let t1;
  if ($[2] !== currentRow || $[3] !== donationCount || $[4] !== triggerHandleAllDonationsSeen) {
    t1 = () => {
      if (currentRow || !donationCount) {
        return;
      }
      triggerHandleAllDonationsSeen();
    };
    $[2] = currentRow;
    $[3] = donationCount;
    $[4] = triggerHandleAllDonationsSeen;
    $[5] = t1;
  } else {
    t1 = $[5];
  }
  let t2;
  if ($[6] !== currentRow || $[7] !== donationCount) {
    t2 = [currentRow, donationCount];
    $[6] = currentRow;
    $[7] = donationCount;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  useEffect(t1, t2);
}
function ProgressAndDonations(t0) {
  const $ = _c(42);
  const {
    receivedAmountInCents,
    receivedDonationsCount,
    sponsor,
    target,
    fundraisingEventId,
    donationFormUrl,
    donations: initialDonations,
    initialDonationLimit
  } = t0;
  const handleDonationFeedError = useHandleDbError();
  let t1;
  if ($[0] !== handleDonationFeedError) {
    t1 = event => {
      handleDonationFeedError(event.data as ErrorMessage);
    };
    $[0] = handleDonationFeedError;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  const messageHandler = t1;
  const [offset, setOffset] = useState(0);
  const [currentRow, setCurrentRow] = useState(0);
  const [limit, setLimit] = useState(initialDonationLimit);
  const [key, setKey] = useState(0);
  let t2;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = () => {
      setKey(_temp);
    };
    $[2] = t2;
  } else {
    t2 = $[2];
  }
  const incrementKey = t2;
  const donationCount = useGetTotalDonationCount(fundraisingEventId);
  const donationAmountSum = useGetTotalDonationAmount(fundraisingEventId);
  const loadedDonationCount = useGetLoadedDonationCount(fundraisingEventId);
  const {
    data: donations,
    offset: loadedOffset,
    row: loadedRow,
    lastSeenDonationPosition
  } = useGetDonations(fundraisingEventId, limit, offset, currentRow);
  useOnViewNewDonations(fundraisingEventId, currentRow, donationCount);
  const insertInitialDonations = useInsertInitialDonations(fundraisingEventId, initialDonations, receivedDonationsCount, receivedAmountInCents);
  let t3;
  if ($[3] !== initialDonationLimit || $[4] !== initialDonations || $[5] !== insertInitialDonations || $[6] !== receivedAmountInCents || $[7] !== receivedDonationsCount) {
    t3 = () => {
      setOffset(0);
      setCurrentRow(0);
      setLimit(initialDonationLimit);
      incrementKey();
      insertInitialDonations(initialDonations, receivedDonationsCount, receivedAmountInCents).then().catch();
    };
    $[3] = initialDonationLimit;
    $[4] = initialDonations;
    $[5] = insertInitialDonations;
    $[6] = receivedAmountInCents;
    $[7] = receivedDonationsCount;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const reset = t3;
  const workerRef = useIndexedDbWorker(workerUrl, fundraisingEventId, messageHandler, reset);
  let t4;
  if ($[9] !== fundraisingEventId || $[10] !== workerRef) {
    t4 = row => {
      workerRef.current?.postMessage(constructRandomAccessMessage(fundraisingEventId, row));
    };
    $[9] = fundraisingEventId;
    $[10] = workerRef;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const fetchMore = t4;
  const fetchMoreDebounced = useDebounce(fetchMore, 300);
  let t5;
  if ($[12] !== fetchMoreDebounced || $[13] !== workerRef) {
    t5 = t6 => {
      const [startIndex, endIndex, row_0] = t6;
      const worker = workerRef.current;
      if (!worker) {
        return;
      }
      fetchMoreDebounced(row_0);
      setOffset(startIndex);
      setCurrentRow(row_0);
      setLimit(endIndex - startIndex + 1);
    };
    $[12] = fetchMoreDebounced;
    $[13] = workerRef;
    $[14] = t5;
  } else {
    t5 = $[14];
  }
  const onScroll = t5;
  let t6;
  if ($[15] !== currentRow || $[16] !== donationAmountSum || $[17] !== donationCount || $[18] !== donations || $[19] !== fundraisingEventId || $[20] !== lastSeenDonationPosition || $[21] !== loadedOffset || $[22] !== loadedRow || $[23] !== onScroll) {
    t6 = {
      donations,
      donationCount,
      donationAmountSum,
      fundraisingEventId,
      offset: loadedOffset,
      row: loadedRow,
      requestedRow: currentRow,
      onScroll,
      newDonationsOffset: lastSeenDonationPosition,
      totalDonationOffset: 0
    };
    $[15] = currentRow;
    $[16] = donationAmountSum;
    $[17] = donationCount;
    $[18] = donations;
    $[19] = fundraisingEventId;
    $[20] = lastSeenDonationPosition;
    $[21] = loadedOffset;
    $[22] = loadedRow;
    $[23] = onScroll;
    $[24] = t6;
  } else {
    t6 = $[24];
  }
  const values = t6;
  let t7;
  if ($[25] !== receivedAmountInCents || $[26] !== receivedDonationsCount || $[27] !== sponsor || $[28] !== target) {
    t7 = <Progress sponsor={sponsor} target={target} receivedAmountInCents={receivedAmountInCents} receivedDonationsCount={receivedDonationsCount} />;
    $[25] = receivedAmountInCents;
    $[26] = receivedDonationsCount;
    $[27] = sponsor;
    $[28] = target;
    $[29] = t7;
  } else {
    t7 = $[29];
  }
  const t8 = loadedDonationCount ?? donations.length;
  let t9;
  if ($[30] !== donationFormUrl || $[31] !== fundraisingEventId || $[32] !== initialDonationLimit || $[33] !== initialDonations || $[34] !== key || $[35] !== receivedDonationsCount || $[36] !== t8) {
    t9 = <DonationFeed key={key} fundraisingEventId={fundraisingEventId} donationFormUrl={donationFormUrl} receivedDonationsCount={receivedDonationsCount} maxRows={t8} initialDonations={initialDonations} initialDonationLimit={initialDonationLimit} />;
    $[30] = donationFormUrl;
    $[31] = fundraisingEventId;
    $[32] = initialDonationLimit;
    $[33] = initialDonations;
    $[34] = key;
    $[35] = receivedDonationsCount;
    $[36] = t8;
    $[37] = t9;
  } else {
    t9 = $[37];
  }
  let t10;
  if ($[38] !== t7 || $[39] !== t9 || $[40] !== values) {
    t10 = <ProgressAndDonationsProvider value={values}>{t7}{t9}</ProgressAndDonationsProvider>;
    $[38] = t7;
    $[39] = t9;
    $[40] = values;
    $[41] = t10;
  } else {
    t10 = $[41];
  }
  return t10;
}
function _temp(prevKey) {
  return prevKey + 1;
}
export default ProgressAndDonations;